import { useState } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  ShieldCheck,
  Landmark,
  FileSpreadsheet,
  Plus,
  X,
  Wallet,
  Repeat,
  PieChart,
  FileText,
  Lock,
  Compass,
  HeartPulse,
  Heart,
  Umbrella,
  Users,
  Building2,
  User,
  Home,
  Briefcase,
  Car,
  GraduationCap,
  Building,
  Banknote,
  FileCheck,
  Calculator,
  Receipt,
  MessageCircle,
} from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const services = [
  {
    id: "investments",
    icon: TrendingUp,
    title: "Investments",
    desc: "Build long-term wealth through carefully selected investment opportunities.",
    items: [
      { label: "Demat Account", icon: Wallet },
      { label: "SIP", icon: Repeat },
      { label: "Mutual Funds", icon: PieChart },
      { label: "Bonds", icon: FileText },
      { label: "Fixed Deposits", icon: Lock },
      { label: "Portfolio Guidance", icon: Compass },
    ],
  },
  {
    id: "insurance",
    icon: ShieldCheck,
    title: "Insurance",
    desc: "Protect your future with comprehensive insurance solutions.",
    items: [
      { label: "Health Insurance", icon: HeartPulse },
      { label: "Life Insurance", icon: Heart },
      { label: "General Insurance", icon: Umbrella },
      { label: "Motor Insurance", icon: Car },
      { label: "Family Protection", icon: Users },
      { label: "Corporate Insurance", icon: Building2 },
    ],
  },
  {
    id: "loans",
    icon: Landmark,
    title: "Loan",
    desc: "Access the right financial support with trusted lending partners.",
    items: [
      { label: "Personal Loan", icon: User },
      { label: "Home Loan", icon: Home },
      { label: "Business Loan", icon: Briefcase },
      { label: "Vehicle Loan", icon: Car },
      { label: "Education Loan", icon: GraduationCap },
      { label: "Loan Against Property", icon: Building },
      { label: "Working Capital", icon: Banknote },
    ],
  },
  {
    id: "tax",
    icon: FileSpreadsheet,
    title: "Income Tax Services",
    desc: "Professional tax filing and planning, made simple.",
    items: [
      { label: "Income Tax Filing", icon: FileCheck },
      { label: "Tax Planning", icon: Calculator },
      { label: "GST Support", icon: Receipt },
      { label: "Capital Gains", icon: TrendingUp },
      { label: "Tax Consultation", icon: MessageCircle },
    ],
  },
];

export default function Services() {
  const [flipped, setFlipped] = useState(() => services.map(() => false));

  const toggle = (idx) =>
    setFlipped((f) => f.map((v, i) => (i === idx ? !v : v)));

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#F5F5F0]/50 border-y border-[#EFEFEA]"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-10 md:pt-14 pb-20 md:pb-28">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-start gap-10 lg:gap-16 mb-14">
          <div>
            <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-[#C9A227]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
              Services
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-[42px] leading-[1.05] tracking-tight text-[#0F172A]">
              Every financial need,
              <br />
              <span className="italic">under one calm roof.</span>
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-[1.75] text-[#475569]">
            Four focused verticals, one clear philosophy — guide, protect, and
            grow. Tap a card to see what's inside.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 justify-items-center">
          {services.map((s, idx) => {
            const Icon = s.icon;
            const isFlipped = flipped[idx];
            return (
              <motion.div
                key={s.id}
                data-testid={`service-card-${s.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, ease, delay: idx * 0.08 }}
                onClick={() => toggle(idx)}
                onMouseEnter={() =>
                  setFlipped((f) => f.map((v, i) => (i === idx ? true : v)))
                }
                onMouseLeave={() =>
                  setFlipped((f) => f.map((v, i) => (i === idx ? false : v)))
                }
                role="button"
                tabIndex={0}
                onFocus={() =>
                  setFlipped((f) => f.map((v, i) => (i === idx ? true : v)))
                }
                onBlur={() =>
                  setFlipped((f) => f.map((v, i) => (i === idx ? false : v)))
                }
                aria-pressed={isFlipped}
                className="group relative cursor-pointer select-none aspect-square w-full max-w-[280px]"
                style={{ perspective: "1600px" }}
              >
                {/* Decorative golden outline arc at the base of the circle */}
                <div className="pointer-events-none absolute -inset-[6px] rounded-full border-[3px] border-transparent border-b-[#C9A227]/70" />

                <motion.div
                  className="relative h-full w-full"
                  style={{ transformStyle: "preserve-3d" }}
                  animate={{ rotateY: isFlipped ? 180 : 0 }}
                  transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* FRONT — icon + heading only */}
                  <div
                    className="absolute inset-0 rounded-full bg-white border border-[#EFEFEA] hover:-translate-y-1 hover:shadow-[0_28px_50px_-25px_rgba(15,23,42,0.18)] transition-transform duration-500 overflow-hidden flex flex-col items-center justify-center text-center gap-4 px-8"
                    style={{ backfaceVisibility: "hidden" }}
                  >
                    <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#EFEFEA] bg-[#FAFAF8] text-[#0F172A]">
                      <span className="absolute inset-0 rounded-full border border-[#C9A227]/30 animate-[ping_2.5s_ease-in-out_infinite]" />
                      <Icon size={22} strokeWidth={1.5} />
                    </span>

                    <h3 className="font-display text-xl leading-tight text-[#0F172A]">
                      {s.title}
                    </h3>
                  </div>

                  {/* BACK — golden-outlined circle + tags */}
                  <div
                    className="absolute inset-0 rounded-full bg-[#0F172A] border border-[#0F172A] flex flex-col items-center justify-center text-center gap-2.5 overflow-hidden px-10"
                    style={{
                      backfaceVisibility: "hidden",
                      transform: "rotateY(180deg)",
                    }}
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-[#C9A227] text-[#C9A227] bg-white/5 shadow-[0_0_0_5px_rgba(201,162,39,0.12)]">
                      <Icon size={18} strokeWidth={1.5} />
                    </span>

                    <div className="grid grid-cols-2 gap-1.5 max-w-[200px] justify-items-center">
                      {s.items.map((it) => {
                        const ItemIcon = it.icon;
                        return (
                          <span
                            key={it.label}
                            className="inline-flex items-center gap-1 rounded-full border border-[#C9A227]/40 bg-white/5 px-2 py-0.5 text-[10.5px] leading-tight text-[#F5EFD9]"
                          >
                            <ItemIcon
                              size={10}
                              strokeWidth={1.75}
                              className="text-[#C9A227] shrink-0"
                            />
                            {it.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>

                {/* Toggle badge — sits on the card's rim, never clipped by the circle */}
                <span className="absolute top-2 right-6 h-8 w-8 z-10">
                  {!isFlipped && (
                    <span className="absolute inset-0 rounded-full bg-[#C9A227]/40 animate-ping" />
                  )}
                  <span
                    className={`relative inline-flex h-8 w-8 items-center justify-center rounded-full border transition-all duration-300 ${
                      isFlipped
                        ? "border-[#C9A227]/60 text-[#C9A227] bg-[#0F172A]"
                        : "border-[#C9A227]/50 text-[#C9A227] bg-white group-hover:bg-[#C9A227] group-hover:text-white group-hover:border-[#C9A227]"
                    }`}
                  >
                    {isFlipped ? <X size={14} /> : <Plus size={14} />}
                  </span>
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}